import React from 'react';
import { Add, Edit, Delete, Star } from '@material-ui/icons';
import classNames from 'classnames';

import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  makeStyles,
  createStyles,
  Grid,
  Theme,
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
} from '@material-ui/core';

import locales from '../../../locales';
import Title from '../../Common/Title';
import ItemComponent from './Item';
import ContextButtonComponent from '../../Common/Button/Context';
import { Employees } from '../../../entities';
import { primaryColor } from '../../../theme';
import DeleteDialog from '../Dialog/Delete';

const useStyles = makeStyles((theme: Theme) => createStyles({
  table: {
    '& thead th': {
      textTransform: 'uppercase',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },

  tableWrapper: {
    overflow: "auto",
    maxWidth: "100%"
  },
  // Десктопная версия
  desktopPaper: {
    [theme.breakpoints.down(960)]: {
      display: 'none',
    },
  },
  // Мобильная версия
  mobileCard: {
    display: 'none',
    [theme.breakpoints.down(960)]: {
      display: 'block',
    },
  },
  card: {
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid #E5E5E5',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
  },
  cardContent: {
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  cardContentHeader: {
    display: 'flex',
    justifyContent: "space-between",
    alignItems: 'center'
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 0,
    textDecoration: 'underline',
  },
  cardInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1),
  },
  cardInfoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  cardInfoLabel: {
    fontSize: 12,
    color: '#AAAAAA',
    fontWeight: 500,
  },
  cardInfoValue: {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  cardContacts: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    '& li': {
      marginBottom: theme.spacing(0.5),
      fontSize: 14,
      color: theme.palette.text.primary,
    },
  },
  cardRoles: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    '& li': {
      marginBottom: 0,
      fontSize: 14,
    },
  },
  cardStatus: {
    textTransform: 'uppercase',
  },
  cardStatusActive: {
    color: primaryColor,
  },
  cardInvite: {
    fontSize: 12,
    color: primaryColor,
    textDecoration: 'underline',
    marginTop: theme.spacing(1),
    cursor: 'pointer',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  mobileTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  currentStar: {
    color: primaryColor,
    fontSize: 14,
    marginLeft: 3,
  },
}));

/**
 * Мобильная версия карточки сотрудника
 */
const MobileEmployeeCard: React.FC<{
  employee: GM.Employees.Employee;
  onClick: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete?: (id: number) => void;
  onInvite: (id: number) => void;
  isCurrentEmployee: boolean;
  canBeDeleted: boolean;
  classes: ReturnType<typeof useStyles>;
}> = ({
  employee,
  onClick,
  onEdit,
  onDelete,
  onInvite,
  isCurrentEmployee,
  canBeDeleted,
  classes,
}) => {
  const [isDeleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [isUnableToDeleteAdminAlert, setUnableToDeleteAdminAlert] = React.useState(false);

  const {
    id,
    email,
    phone,
    authorities,
    status,
    createdDate,
  } = employee;

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardContentHeader}>
          <Typography
            component="a"
            href={`/employees/${id}`}
            onClick={(e) => {
              e.preventDefault();
              onClick(id);
            }}
            className={classes.cardTitle}
          >
            {Employees.getName(employee)}
            {isCurrentEmployee && <sup><Star className={classes.currentStar}/></sup>}
          </Typography>

          <div className={classes.cardActions}>
            <IconButton onClick={() => onEdit(id)} size="small">
              <Edit fontSize="small" />
            </IconButton>

            {!canBeDeleted && (
              <>
                <IconButton onClick={() => setUnableToDeleteAdminAlert(true)} size="small">
                  <Delete fontSize="small" color="disabled" />
                </IconButton>

                {isUnableToDeleteAdminAlert && (
                  <Employees.UnableToDeleteAlert onClose={() => setUnableToDeleteAdminAlert(false)} />
                )}
              </>
            )}

            {canBeDeleted && !isCurrentEmployee && onDelete && (
              <>
                <IconButton onClick={() => setDeleteDialogOpen(true)} size="small">
                  <Delete fontSize="small" />
                </IconButton>

                {isDeleteDialogOpen && (
                  <DeleteDialog
                    onClose={() => setDeleteDialogOpen(false)}
                    onDelete={() => onDelete(id)}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className={classes.cardInfo}>
          {(email || phone) && (
            <div className={classes.cardInfoRow}>
              <Typography className={classes.cardInfoLabel}>
                {locales.t('employees.properties.email')}
              </Typography>
              <ul className={classes.cardContacts}>
                {email && <li>{email}</li>}
                {phone && <li>{phone}</li>}
              </ul>
            </div>
          )}

          {authorities && authorities.length > 0 && (
            <div className={classes.cardInfoRow}>
              <Typography className={classes.cardInfoLabel}>
                {locales.t('employees.properties.roles')}
              </Typography>
              <ul className={classes.cardRoles}>
                {authorities.map(authority => (
                  <li key={authority}>
                    {locales.t(`employees.properties.authorities.${authority}.title`)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={classes.cardInfoRow}>
            <Typography className={classes.cardInfoLabel}>
              {locales.t('employees.properties.status')}
            </Typography>
            {(status === 'ACTIVE' && (
              <Typography className={classNames(classes.cardStatus, classes.cardStatusActive)}>
                {locales.t('employees.status_active')}
              </Typography>
            )) || (
              <>
                <Typography className={classes.cardStatus}>
                  {locales.t('employees.status_disabled')}
                </Typography>
                <div
                  className={classes.cardInvite}
                  onClick={() => onInvite(id)}
                  tabIndex={0}
                  role="button"
                >
                  Выслать приглашение
                  {createdDate && ' повторно'}
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ListComponent: React.FC<{
  isLoading: boolean;
  employees: GM.Employees.Employees;
  onClick: (id: number) => void;
  onEdit: (id: number) => void;
  onAdd: () => void;
  onDelete: (id: number) => void;
  onInvite: (id: number) => void;
  currentEmployee: GM.Employees.Employee | null;
}> = ({
  isLoading,
  employees,
  onClick,
  onEdit,
  onAdd,
  onDelete,
  onInvite,
  currentEmployee,
}) => {
  const classes = useStyles();
  const values = Object.values(employees);

  return (
    <Grid item xs={12}>
      {/* Десктопная версия (таблица) */}
      <Paper elevation={0} className={classes.desktopPaper}>
        <Title text={locales.t('employees.title')} borderBottom />

        {isLoading && <Box p={2}>{locales.t('employees.loading')}</Box>}

        {!isLoading && (
          <>
            {values.length > 0 && (
              <div className={classes.tableWrapper}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{locales.t('employees.properties.name')}</TableCell>
                      <TableCell>{locales.t('employees.properties.email')}</TableCell>
                      <TableCell>{locales.t('employees.properties.roles')}</TableCell>
                      <TableCell>{locales.t('employees.properties.status')}</TableCell>
                      <TableCell>{locales.t('employees.properties.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {values.map((employee => {
                      const isCurrentEmployee = (currentEmployee && currentEmployee.id === employee.id) || false;
                      const canBeDeleted = Employees.employeeCanBeDeleted(employee.id, employees);

                      return (
                        <ItemComponent
                          key={employee.id}
                          employee={employee}
                          isCurrentEmployee={isCurrentEmployee}
                          onClick={onClick}
                          onEdit={onEdit}
                          onInvite={onInvite}
                          onDelete={canBeDeleted ? onDelete : undefined}
                          canBeDeleted={canBeDeleted}
                        />
                      );
                    }))}
                  </TableBody>
                </Table>
              </div>
            )}

            {values.length === 0 && <Box p={3}>{locales.t('employees.empty')}</Box>}

            <Box p={2}>
              <ContextButtonComponent
                startIcon={<Add />}
                onClick={onAdd}
                title={locales.t('employees.add')}
                isStrong
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Мобильная версия (карточки) */}
      <Card className={classes.mobileCard} elevation={0}>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.mobileTitle}>
            {locales.t('employees.title')}
          </Typography>

          {isLoading && <Box p={2}>{locales.t('employees.loading')}</Box>}

          {!isLoading && (
            <>
              {values.length > 0 && (
                <>
                  {values.map((employee => {
                    const isCurrentEmployee = (currentEmployee && currentEmployee.id === employee.id) || false;
                    const canBeDeleted = Employees.employeeCanBeDeleted(employee.id, employees);

                    return (
                      <MobileEmployeeCard
                        key={employee.id}
                        employee={employee}
                        isCurrentEmployee={isCurrentEmployee}
                        onClick={onClick}
                        onEdit={onEdit}
                        onInvite={onInvite}
                        onDelete={canBeDeleted ? onDelete : undefined}
                        canBeDeleted={canBeDeleted}
                        classes={classes}
                      />
                    );
                  }))}
                </>
              )}

              {values.length === 0 && <Box p={3}>{locales.t('employees.empty')}</Box>}

              <Box pt={2}>
                <ContextButtonComponent
                  startIcon={<Add />}
                  onClick={onAdd}
                  title={locales.t('employees.add')}
                  isStrong
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ListComponent;
